import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Download, Highlighter, MessageSquare, Search, Tag, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { type HighlightInboxRow, api } from "../../lib/api";
import { cn, formatRelative } from "../../lib/utils";

type KindFilter = "all" | "highlight" | "comment" | "tag";
type ColorFilter = "all" | "yellow" | "green" | "pink" | "blue" | "purple";

interface Filters {
  q: string;
  kind: KindFilter;
  color: ColorFilter;
  project?: string;
  tag?: string;
}

export function HighlightsInbox() {
  const [filters, setFilters] = useState<Filters>({ q: "", kind: "all", color: "all" });
  const [selected, setSelected] = useState<HighlightInboxRow | null>(null);

  const query = useQuery({
    queryKey: ["highlights", filters],
    queryFn: () =>
      api.listHighlights({
        q: filters.q || undefined,
        kind: filters.kind === "all" ? undefined : filters.kind,
        color: filters.color === "all" ? undefined : filters.color,
        project: filters.project || undefined,
        tag: filters.tag || undefined,
        page_size: 100,
      }),
    staleTime: 10_000,
  });

  const items = query.data?.items ?? [];

  const projects = useMemo(() => uniqueValues(items, (it) => it.doc.project), [items]);
  const tags = useMemo(() => uniqueValues(items, null, (it) => it.doc.tags), [items]);

  const exportUrl = api.exportHighlightsUrl({
    q: filters.q || undefined,
    kind: filters.kind === "all" ? undefined : filters.kind,
    project: filters.project || undefined,
  });

  return (
    <div className="flex h-full overflow-hidden">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-(--border-subtle) bg-(--bg-elevated) p-3 md:flex">
        <h2 className="mb-2 text-sm font-semibold">Filtros</h2>
        <FilterGroup label="Tipo">
          {(["all", "highlight", "comment", "tag"] as KindFilter[]).map((k) => (
            <FilterChip
              key={k}
              active={filters.kind === k}
              onClick={() => setFilters((f) => ({ ...f, kind: k }))}
            >
              {kindLabel(k)}
            </FilterChip>
          ))}
        </FilterGroup>

        <FilterGroup label="Color">
          {(["all", "yellow", "green", "pink", "blue", "purple"] as ColorFilter[]).map((c) => (
            <FilterChip
              key={c}
              active={filters.color === c}
              onClick={() => setFilters((f) => ({ ...f, color: c }))}
            >
              {c === "all" ? "Todos" : c}
            </FilterChip>
          ))}
        </FilterGroup>

        {projects.length > 0 && (
          <FilterGroup label="Proyecto">
            {projects.map((p) => (
              <FilterChip
                key={p}
                active={filters.project === p}
                onClick={() =>
                  setFilters((f) => ({ ...f, project: f.project === p ? undefined : p }))
                }
              >
                {p}
              </FilterChip>
            ))}
          </FilterGroup>
        )}

        {tags.length > 0 && (
          <FilterGroup label="Etiqueta">
            {tags.slice(0, 20).map((t) => (
              <FilterChip
                key={t}
                active={filters.tag === t}
                onClick={() => setFilters((f) => ({ ...f, tag: f.tag === t ? undefined : t }))}
              >
                #{t}
              </FilterChip>
            ))}
          </FilterGroup>
        )}

        <a
          href={exportUrl}
          className="mt-auto inline-flex items-center gap-2 rounded-md border border-(--border-subtle) bg-(--bg-base) px-3 py-2 text-xs hover:bg-(--bg-hover)"
          download
          data-testid="highlights-export"
        >
          <Download size={13} /> Exportar Markdown
        </a>
      </aside>

      <main className="flex flex-1 flex-col overflow-y-auto" id="highlights-main">
        <header className="sticky top-0 z-10 border-b border-(--border-subtle) bg-(--bg-base) px-6 py-3">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold">Highlights</h1>
            <Badge variant="muted" className="text-[10px]">
              {items.length}
            </Badge>
            <div className="ml-auto flex max-w-md flex-1 items-center gap-2">
              <div className="relative flex-1">
                <Search
                  size={13}
                  className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-(--text-tertiary)"
                />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  placeholder="Buscar en quotes y notas…"
                  className="h-9 pl-7"
                  data-testid="highlights-q"
                />
              </div>
              {(filters.q || filters.project || filters.tag || filters.kind !== "all") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setFilters({ q: "", kind: "all", color: "all" })}
                >
                  <X size={13} /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </header>

        <div className="grid flex-1 grid-cols-1 gap-0 lg:grid-cols-[1fr_360px]">
          <section className="px-4 py-3 space-y-2" data-testid="highlights-list">
            {query.isLoading && [1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
            {!query.isLoading && items.length === 0 && (
              <div className="rounded-lg border border-dashed border-(--border-strong) p-8 text-center text-sm text-(--text-tertiary)">
                No hay highlights con esos filtros.
              </div>
            )}
            {items.map((row) => (
              <HighlightCard
                key={row.id}
                row={row}
                active={selected?.id === row.id}
                onSelect={() => setSelected(row)}
              />
            ))}
          </section>

          <aside className="hidden border-l border-(--border-subtle) bg-(--bg-elevated) px-4 py-3 lg:block">
            {selected ? <DetailPanel row={selected} /> : <DetailPlaceholder />}
          </aside>
        </div>
      </main>
    </div>
  );
}

function HighlightCard({
  row,
  active,
  onSelect,
}: {
  row: HighlightInboxRow;
  active: boolean;
  onSelect: () => void;
}) {
  const range = parseRange(row.range_json);
  const quote = range?.quote ?? row.body ?? "";
  return (
    <article
      data-testid="highlight-card"
      className={cn(
        "rounded-lg border bg-(--bg-elevated) p-3 transition-colors hover:bg-(--bg-hover) focus-within:ring-1 focus-within:ring-(--accent-500)",
        active ? "border-(--accent-500)" : "border-(--border-subtle)",
      )}
    >
      <header className="flex items-center gap-2 text-xs text-(--text-tertiary)">
        <KindIcon kind={row.kind} color={row.color} />
        <span>{formatRelative(row.created_at)}</span>
        {row.doc.project && (
          <Badge variant="muted" className="text-[10px]">
            {row.doc.project}
          </Badge>
        )}
        {row.doc.tags.slice(0, 3).map((t) => (
          <Badge key={t} variant="outline" className="text-[10px]">
            #{t}
          </Badge>
        ))}
      </header>
      <button type="button" onClick={onSelect} className="mt-1.5 w-full cursor-pointer text-left">
        <p
          className="line-clamp-3 text-sm"
          style={{
            background:
              row.kind === "highlight" && row.color
                ? `var(--highlight-${row.color})`
                : "transparent",
            padding: row.kind === "highlight" ? "0.1em 0.25em" : 0,
            borderRadius: 4,
          }}
        >
          {quote}
        </p>
      </button>
      {row.kind === "comment" && row.body && (
        <p className="mt-1.5 text-xs text-(--text-secondary)">
          <strong>{row.author_subject}:</strong> {row.body}
        </p>
      )}
      {row.kind === "tag" && row.body && (
        <p className="mt-1.5 text-xs text-(--text-secondary)">#{row.body}</p>
      )}
      <footer className="mt-2 flex items-center justify-between text-xs">
        <Link
          to="/docs/$docId"
          params={{ docId: row.doc_id }}
          hash={range?.blockId ? `block-${range.blockId}` : undefined}
          className="text-(--accent-500) hover:underline"
        >
          {row.doc.title ?? row.doc.path ?? row.doc_id} →
        </Link>
      </footer>
    </article>
  );
}

function DetailPanel({ row }: { row: HighlightInboxRow }) {
  const range = parseRange(row.range_json);
  return (
    <div className="space-y-3 text-sm">
      <header className="flex items-center gap-2 border-b border-(--border-subtle) pb-2">
        <KindIcon kind={row.kind} color={row.color} />
        <strong className="text-(--text-primary)">{row.author_subject}</strong>
        <span className="text-xs text-(--text-tertiary)">{formatRelative(row.created_at)}</span>
      </header>
      <blockquote className="border-l-2 border-(--accent-300) pl-3 italic">
        {range?.quote ?? row.body ?? "(sin texto)"}
      </blockquote>
      {row.kind === "comment" && row.body && (
        <div>
          <h3 className="text-xs font-semibold uppercase text-(--text-tertiary)">Nota</h3>
          <p>{row.body}</p>
        </div>
      )}
      {row.kind === "tag" && row.body && (
        <div>
          <h3 className="text-xs font-semibold uppercase text-(--text-tertiary)">Etiqueta</h3>
          <p>#{row.body}</p>
        </div>
      )}
      <div className="text-xs text-(--text-tertiary)">
        <p>Documento</p>
        <p className="text-(--text-secondary)">{row.doc.path ?? row.doc_id}</p>
      </div>
      <Link
        to="/docs/$docId"
        params={{ docId: row.doc_id }}
        hash={range?.blockId ? `block-${range.blockId}` : undefined}
      >
        <Button variant="primary" size="sm" className="w-full">
          Abrir documento →
        </Button>
      </Link>
    </div>
  );
}

function DetailPlaceholder() {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center text-sm text-(--text-tertiary)">
      <Highlighter size={28} className="mb-2 text-(--text-disabled)" />
      <p>Selecciona un highlight para verlo en detalle.</p>
    </div>
  );
}

function KindIcon({
  kind,
  color,
}: {
  kind: HighlightInboxRow["kind"];
  color: HighlightInboxRow["color"];
}) {
  if (kind === "comment") return <MessageSquare size={11} className="text-(--accent-500)" />;
  if (kind === "tag") return <Tag size={11} className="text-(--accent-500)" />;
  return (
    <Highlighter
      size={11}
      style={{ color: color ? `var(--highlight-${color})` : "var(--accent-500)" }}
    />
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </h3>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] transition-colors",
        active
          ? "bg-(--accent-500) text-white"
          : "bg-(--bg-base) text-(--text-secondary) hover:bg-(--bg-hover)",
      )}
    >
      {children}
    </button>
  );
}

function kindLabel(k: KindFilter): string {
  if (k === "all") return "Todos";
  if (k === "highlight") return "Subrayados";
  if (k === "comment") return "Comentarios";
  return "Etiquetas";
}

function uniqueValues<T>(
  rows: T[],
  selector: ((row: T) => string | null) | null,
  multi?: (row: T) => string[],
): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    if (multi) {
      for (const v of multi(r)) if (v) set.add(v);
    } else if (selector) {
      const v = selector(r);
      if (v) set.add(v);
    }
  }
  return Array.from(set).sort();
}

function parseRange(json: string): { blockId?: string; quote?: string } | null {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}
